import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  evaluateNavEntitlementFromVisible,
  type EntitlementBlockKey,
} from 'pi-kiosk-shared/tenant-entitlements';
import { hasAnyEffectiveCapability, hasEffectiveCapability } from 'pi-kiosk-shared';
import { isEnterpriseNavItemDeployEnabled } from '../../../config/featureFlags.js';
import { useAuth } from '../../auth/hooks/useAuth.js';
import type { EnterpriseNavItem } from '../../dashboard/components/EnterpriseGroupedNav/enterpriseNavGroups.js';
import type { AdminEntitlementSnapshot } from '../types/adminEntitlementSnapshot.js';
import {
  isEntitlementBlockActionAllowed,
  isEntitlementBlockNavVisible,
  isEntitlementBlockReadOnly,
  type EntitlementRouteAction,
} from '../utils/blockAccess.js';
import { resolveNavCapabilityRequirement } from '../utils/navCapability.js';
import {
  isUrlTenantInSessionScope,
  type UrlTenantScopeBindInput,
} from '../utils/urlTenantScopeBind.js';
import { useLoyaltyModuleHealth } from '../../loyalty/hooks/useLoyaltyModuleHealth.js';
import {
  isTenantScopedComplianceNavItem,
  resolveAdminComplianceTenantId,
  type ResolveAdminComplianceTenantIdInput,
} from '../../compliance/utils/resolveAdminComplianceTenantId.js';

/**
 * Tenant-nav compliance blocks that require a resolvable compliance tenant
 * (Option B — no URL→tenantId SoT). GDPR/consent UI is platform-only on
 * `/dev/compliance` — not gated via tenant entitlement nav.
 */
export const COMPLIANCE_ENTITLEMENT_BLOCKS: ReadonlySet<EntitlementBlockKey> = new Set([
  'audit_logs_admin_ui',
]);

export function requiresResolvableComplianceTenant(blockKey: EntitlementBlockKey): boolean {
  return COMPLIANCE_ENTITLEMENT_BLOCKS.has(blockKey);
}

export interface EvaluateBlockNavVisibleInput {
  readonly snapshot: AdminEntitlementSnapshot | null;
  readonly blockKey: EntitlementBlockKey;
  /** Resolved numeric tenant; undefined hides compliance entitlement blocks only. */
  readonly complianceTenantId: number | undefined;
}

/**
 * Entitlement nav-visible ∧ (for compliance blocks only) resolvable tenant.
 * Capability is irrelevant — callers that need capability use canShowEnterpriseNavItem.
 */
export function evaluateBlockNavVisible({
  snapshot,
  blockKey,
  complianceTenantId,
}: EvaluateBlockNavVisibleInput): boolean {
  if (!isEntitlementBlockNavVisible(snapshot, blockKey)) {
    return false;
  }
  if (requiresResolvableComplianceTenant(blockKey) && complianceTenantId === undefined) {
    return false;
  }
  return true;
}

export interface EvaluateBlockActionAllowedInput {
  readonly snapshot: AdminEntitlementSnapshot | null;
  readonly blockKey: EntitlementBlockKey;
  readonly action?: EntitlementRouteAction;
  readonly complianceTenantId: number | undefined;
}

/**
 * Entitlement action-allowed ∧ (for compliance blocks only) resolvable tenant.
 */
export function evaluateBlockActionAllowed({
  snapshot,
  blockKey,
  action = 'read',
  complianceTenantId,
}: EvaluateBlockActionAllowedInput): boolean {
  if (!isEntitlementBlockActionAllowed(snapshot, blockKey, action)) {
    return false;
  }
  if (requiresResolvableComplianceTenant(blockKey) && complianceTenantId === undefined) {
    return false;
  }
  return true;
}

export interface UseTenantEntitlementResult {
  readonly snapshot: AdminEntitlementSnapshot | null;
  readonly revision: number | null;
  readonly isLoading: boolean;
  readonly isBlockNavVisible: (blockKey: EntitlementBlockKey) => boolean;
  readonly isBlockActionAllowed: (blockKey: EntitlementBlockKey, action?: EntitlementRouteAction) => boolean;
  readonly isBlockReadOnly: (blockKey: EntitlementBlockKey) => boolean;
  readonly canShowNavItem: (item: EnterpriseNavItem) => boolean;
  readonly refreshEntitlement: () => Promise<void>;
}

export interface EvaluateComplianceNavVisibilityInput {
  readonly item: EnterpriseNavItem;
  readonly snapshot: AdminEntitlementSnapshot | null;
  readonly grants: readonly string[];
  readonly tenantResolveInput: ResolveAdminComplianceTenantIdInput;
}

/**
 * Nav visibility = entitlementSnapshot ∧ clipped commercial `/admin/me` capabilities
 * ∧ (optional) URL tenantScope bind — same bind as SectionAccessGate / evaluateSectionAccess.
 * Pass `grants` from auth me.capabilities (BE me-clip); do not re-clip on the client.
 *
 * When `urlTenantBind` is omitted or URL tenant is empty (platform shell), tenantScope is not
 * applied — tenant routes always pass a bind so nav cannot show while SAG denies on scope.
 */
export function evaluateNavItemAccess(
  item: EnterpriseNavItem,
  snapshot: AdminEntitlementSnapshot | null,
  grants: readonly string[],
  urlTenantBind?: UrlTenantScopeBindInput | null,
): boolean {
  const urlTenant = urlTenantBind?.urlTenantCode.trim() ?? '';
  if (urlTenant.length > 0 && urlTenantBind != null && !isUrlTenantInSessionScope(urlTenantBind)) {
    return false;
  }

  const entitlementVisible = (() => {
    if (item.entitlementAllOf !== undefined && item.entitlementAllOf.length > 0) {
      return item.entitlementAllOf.every((key) => isEntitlementBlockNavVisible(snapshot, key));
    }
    if (item.entitlementAnyOf !== undefined && item.entitlementAnyOf.length > 0) {
      return item.entitlementAnyOf.some((key) => isEntitlementBlockNavVisible(snapshot, key));
    }
    const blockKey = item.entitlementBlockKey;
    return blockKey === undefined ? true : isEntitlementBlockNavVisible(snapshot, blockKey);
  })();

  const requirement = resolveNavCapabilityRequirement(item);

  if (requirement.capability !== undefined) {
    return evaluateNavEntitlementFromVisible({
      entitlementVisible,
      grants,
      requiredCapability: requirement.capability,
    });
  }

  if (requirement.allOf !== undefined && requirement.allOf.length > 0) {
    return (
      entitlementVisible &&
      requirement.allOf.every((cap) => hasEffectiveCapability(grants, cap))
    );
  }

  if (requirement.anyOf !== undefined && requirement.anyOf.length > 0) {
    return entitlementVisible && hasAnyEffectiveCapability(grants, requirement.anyOf);
  }

  // account-settings and unmapped sections: entitlement-only (capability map may be empty).
  if (item.section === 'account-settings') {
    return entitlementVisible;
  }

  return entitlementVisible;
}

export interface CanShowEnterpriseNavItemInput {
  readonly item: EnterpriseNavItem;
  readonly snapshot: AdminEntitlementSnapshot | null;
  /** Clipped commercial `/admin/me` capabilities (BE me-clip). */
  readonly grants: readonly string[];
  /** Resolved numeric tenant for tenant-scoped compliance nav; undefined ⇒ hide. */
  readonly complianceTenantId: number | undefined;
  /** Loyalty-program only; undefined/false hides the item. */
  readonly loyaltyModuleEnabled?: boolean;
  /**
   * URL ↔ session tenantScope bind (same as SectionAccessGate).
   * When URL tenant is non-empty and bind fails, hide nav (no visible-then-deny).
   */
  readonly urlTenantBind?: UrlTenantScopeBindInput | null;
}

/**
 * Pure canShowNavItem gate: entitlementSnapshot ∧ clipped me.capabilities
 * ∧ tenantScope (when URL tenant present) ∧ compliance tenant ∧ loyalty.
 *
 * `grants` must be commercial `/admin/me` clipped capabilities (BE me-clip).
 *
 * Option B: platform-* / multi-tenant without a resolved numeric tenant
 * ⇒ audit-logs stays non-navigable until TenantSwitcher binds one.
 */
export function canShowEnterpriseNavItem({
  item,
  snapshot,
  grants,
  complianceTenantId,
  loyaltyModuleEnabled,
  urlTenantBind,
}: CanShowEnterpriseNavItemInput): boolean {
  // Deploy Vite FF before entitlement — HARD-HIDE Platby / paid Explore when FF off.
  if (!isEnterpriseNavItemDeployEnabled(item.id)) {
    return false;
  }
  if (!evaluateNavItemAccess(item, snapshot, grants, urlTenantBind)) {
    return false;
  }
  if (isTenantScopedComplianceNavItem(item) && complianceTenantId === undefined) {
    return false;
  }
  if (item.id !== 'loyalty-program') {
    return true;
  }
  return loyaltyModuleEnabled === true;
}

/**
 * Thin wrapper: resolve tenant from auth-shaped input then apply canShowEnterpriseNavItem
 * (loyalty omitted — callers that need loyalty use canShowEnterpriseNavItem directly).
 */
export function evaluateComplianceNavVisibility({
  item,
  snapshot,
  grants,
  tenantResolveInput,
}: EvaluateComplianceNavVisibilityInput): boolean {
  return canShowEnterpriseNavItem({
    item,
    snapshot,
    grants,
    complianceTenantId: resolveAdminComplianceTenantId(tenantResolveInput),
  });
}

export function useTenantEntitlement(): UseTenantEntitlementResult {
  const auth = useAuth();
  const { tenant: routeTenant } = useParams<{ tenant?: string }>();
  // Soft bind — do not throw on platform routes without :tenant (unlike Dashboard coerce).
  const urlTenantCode = routeTenant?.trim() ?? '';
  const entitlementSnapshot = auth.entitlementSnapshot ?? null;
  /** Clipped commercial `/admin/me` capabilities (BE me-clip); platform me is unclipped. */
  const capabilities = useMemo(
    () => auth.capabilities ?? [],
    [auth.capabilities],
  );
  const { isUserLoading, refreshUser, token, isAuthenticated } = auth;

  const urlTenantBind = useMemo((): UrlTenantScopeBindInput | null => {
    if (urlTenantCode.trim().length === 0) {
      return null;
    }
    return {
      urlTenantCode,
      homeTenantCode: auth.homeTenantCode,
      tenantScope: auth.tenantScope,
      tenantScopeCodes: auth.tenantScopeCodes,
    };
  }, [auth.homeTenantCode, auth.tenantScope, auth.tenantScopeCodes, urlTenantCode]);
  // G2 — cookie-capable sessions: do not hard-block on null memory JWT.
  const sessionActive = isAuthenticated || Boolean(token);

  const swrKey =
    sessionActive && entitlementSnapshot != null
      ? (['tenant-entitlement-snapshot', entitlementSnapshot.revision] as const)
      : null;

  useSWR(
    swrKey,
    () => entitlementSnapshot,
    {
      fallbackData: entitlementSnapshot ?? undefined,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const loyaltyNavEligible = useMemo(
    () =>
      entitlementSnapshot != null &&
      isEntitlementBlockNavVisible(entitlementSnapshot, 'loyalty_program'),
    [entitlementSnapshot],
  );

  /** G-LOYALTY-OWNER: sole subscriber on loyalty-module-health key (no second useSWR). */
  const { health: loyaltyHealth } = useLoyaltyModuleHealth(
    sessionActive && loyaltyNavEligible,
  );

  const complianceTenantId = useMemo(
    () =>
      resolveAdminComplianceTenantId({
        userTenantId: auth.user?.tenantId,
        currentTenantId: auth.currentTenantId,
        tenantScope: auth.tenantScope,
      }),
    [auth.currentTenantId, auth.tenantScope, auth.user?.tenantId],
  );

  const isBlockNavVisible = useCallback(
    (blockKey: EntitlementBlockKey): boolean =>
      evaluateBlockNavVisible({
        snapshot: entitlementSnapshot,
        blockKey,
        complianceTenantId,
      }),
    [complianceTenantId, entitlementSnapshot],
  );

  const isBlockActionAllowed = useCallback(
    (blockKey: EntitlementBlockKey, action: EntitlementRouteAction = 'read'): boolean =>
      evaluateBlockActionAllowed({
        snapshot: entitlementSnapshot,
        blockKey,
        action,
        complianceTenantId,
      }),
    [complianceTenantId, entitlementSnapshot],
  );

  const isBlockReadOnly = useCallback(
    (blockKey: EntitlementBlockKey): boolean =>
      isEntitlementBlockReadOnly(entitlementSnapshot, blockKey),
    [entitlementSnapshot],
  );

  const canShowNavItem = useCallback(
    (item: EnterpriseNavItem): boolean =>
      canShowEnterpriseNavItem({
        item,
        snapshot: entitlementSnapshot,
        grants: capabilities,
        complianceTenantId,
        loyaltyModuleEnabled: loyaltyHealth?.moduleEnabled,
        urlTenantBind,
      }),
    [capabilities, complianceTenantId, entitlementSnapshot, loyaltyHealth, urlTenantBind],
  );

  const refreshEntitlement = useCallback(async (): Promise<void> => {
    await refreshUser();
  }, [refreshUser]);

  return useMemo(
    () => ({
      snapshot: entitlementSnapshot,
      revision: entitlementSnapshot?.revision ?? null,
      isLoading: isUserLoading,
      isBlockNavVisible,
      isBlockActionAllowed,
      isBlockReadOnly,
      canShowNavItem,
      refreshEntitlement,
    }),
    [
      entitlementSnapshot,
      isUserLoading,
      isBlockNavVisible,
      isBlockActionAllowed,
      isBlockReadOnly,
      canShowNavItem,
      refreshEntitlement,
    ],
  );
}
